import {computed, makeAutoObservable} from 'mobx';
import {TRISTATE_CHECKBOX_VALUES} from '../../../tristate-checkbox/tristate-checkbox';
import {CHECKBOX_TREE_COUNT_MODE, ICheckboxesTreeItem} from "./checkboxes-tree-store";

/**
 * Store for a single nested checkbox item. Holds model/changes, computes parent/children state,
 * and exposes helpers for rendering and interactions.
 */
class CheckboxesTreeItemStore {
    private readonly _model: ICheckboxesTreeItem;
    private readonly _pathIndex?: string;
    private readonly _parentStore?: CheckboxesTreeItemStore;
    private _changes: ICheckboxesTreeItem = {} as ICheckboxesTreeItem;
    private _childrenStores?: CheckboxesTreeItemStore[];
    private _isHidden: boolean = false;
    private readonly _labelLower: string = '';

    /** Creates an item store and computes the initial checkbox state for its subtree. */
    public constructor(model: ICheckboxesTreeItem, pathIndex: string, parentStore?: CheckboxesTreeItemStore) {
        this._model = model;
        this._pathIndex = pathIndex;
        this._parentStore = parentStore;
        this._labelLower = (this._model.label || '').toLowerCase();
        makeAutoObservable(this, {
            totalLeafCount: computed,
            selectedLeafCount: computed,
            selectedChildCount: computed,
            partiallySelectedChildCount: computed,
            displayText: computed
        }, {autoBind: true});
        this.computeAndSetInitialState();
    }

    /** Returns item value (identifier). */
    public value(): string {
        return this._model.value;
    }

    /** Returns display label for the item. */
    public label(): string {
        return this._model.label
    }

    /** Returns lowercased label cached on construction. */
    public labelLower(): string {
        return this._labelLower;
    }

    /** Returns current checkbox state (changes an override model). */
    public getCheckBoxValue(): TRISTATE_CHECKBOX_VALUES | undefined {
        return this._changes.checkBoxValue ?? this._model.checkBoxValue;
    }

    /** Sets checkbox state in the change layer. */
    public setCheckBoxValue(newState: TRISTATE_CHECKBOX_VALUES | undefined): void {
        this._changes.checkBoxValue = newState;
    }

    /** Sets the current visibility flag calculated by filter. */
    public setHidden(hidden: boolean): void {
        if (this._isHidden === hidden) {
            return;
        }
        this._isHidden = hidden;
    }

    /** Returns raw children from the model. */
    public getChildren(): ICheckboxesTreeItem[] {
        return this._model.childNodes ?? [];
    }

    /** Lazily creates and returns children item stores. */
    public getChildrenStores(): CheckboxesTreeItemStore[] {
        if (!this._childrenStores) {
            const children = this.getChildren();
            this._childrenStores = children.map((child, idx) => new CheckboxesTreeItemStore(child, `${this._pathIndex}/${idx}`, this));
        }
        return this._childrenStores;
    }

    /** Returns true if a node has any children. */
    public hasChildren(): boolean {
        const children = this.getChildren();
        return Array.isArray(children) && children.length > 0;
    }

    /** Returns whether this item is disabled. */
    public isDisabled(): boolean {
        return Boolean(this._model.disabled);
    }

    /** Returns whether the filter currently hides this item. */
    public isHidden(): boolean {
        return this._isHidden;
    }

    /** Returns path index (stable id within the tree). */
    public pathIndex(): string {
        return this._pathIndex!;
    }

    /** Applies checkbox change to this subtree and propagates to parents. */
    public applyCheckboxChange(newState: TRISTATE_CHECKBOX_VALUES): void {
        this.updateSubtree(newState);
        this.updateParentCheckboxes();
    }

    /** Derives parent's tristate value from its children. */
    public calculateParentStateByChildrenValue(): TRISTATE_CHECKBOX_VALUES {
        const childStates = this.getChildrenStores().map((c) => c.getCheckBoxValue());
        const total = childStates.length;

        if (total === 0) {
            return this.getCheckBoxValue() ?? TRISTATE_CHECKBOX_VALUES.UNCHECKED;
        }

        const checked = childStates.filter((s) => s === TRISTATE_CHECKBOX_VALUES.CHECKED).length;
        const partial = childStates.filter((s) => s === TRISTATE_CHECKBOX_VALUES.PARTIALLY_CHECKED).length;

        if (checked === total) {
            return TRISTATE_CHECKBOX_VALUES.CHECKED;
        }

        if (checked > 0 || partial > 0) {
            return TRISTATE_CHECKBOX_VALUES.PARTIALLY_CHECKED;
        }

        return TRISTATE_CHECKBOX_VALUES.UNCHECKED;
    }

    /**
     * Returns summary segments for the closed dropdown input.
     * Nodes counting direct children expand to child segments while partially selected.
     */
    public getSummaryParts(): string[] {
        if (!this.hasChildren()) {
            return this.getCheckBoxValue() === TRISTATE_CHECKBOX_VALUES.CHECKED ? [this.label()] : [];
        }

        if (this.selectedUnitCount === 0) {
            return [];
        }

        if (this.isFullySelected) {
            return [`${this.label()} (all)`];
        }

        if (this._countsDirectChildren()) {
            return this.getChildrenStores().flatMap((child) => child.getSummaryParts());
        }

        return [`${this.label()} (${this.selectedUnitCount})`];
    }

    /** Returns a computed display text based on cached aggregates. */
    public get displayText(): string {
        if (!this.hasChildren()) {
            return this.label();
        }

        if (this.isFullySelected) {
            return `${this.label()} (all)`;
        }

        const selected = this.selectedUnitCount;

        return selected > 0 ? `${this.label()} (${selected})` : this.label();
    }

    /** Returns true if all descendants are disabled. */
    public areAllChildrenDisabled(): boolean {
        const childrenStores = this.getChildrenStores();
        if (!childrenStores || childrenStores.length === 0) {
            return false;
        }
        return childrenStores.every((child) => {
            if (child.isDisabled()) {
                return true;
            }
            if (child.hasChildren()) {
                return child.areAllChildrenDisabled();
            }
            return false;
        });
    }

    /** Returns merged node (model + changes) and merged children recursively. */
    public getUpdatedModel(): ICheckboxesTreeItem {
        const updatedChildren = this.getChildrenStores().map((c) => c.getUpdatedModel());
        return {...this._model, ...this._changes, childNodes: updatedChildren};
    }

    /** Recursively updates ancestors' tristate derived from changed children. */
    private updateParentCheckboxes(): void {
        const parent = this._parentStore;

        if (!parent) {
            return;
        }

        const parentState = parent.calculateParentStateByChildrenValue();
        parent.setCheckBoxValue(parentState);
        parent.updateParentCheckboxes();
    }

    /** Recursively applies checkbox state to all descendants or to the leaf. */
    private updateSubtree(newState: TRISTATE_CHECKBOX_VALUES): void {
        if (this.isDisabled()) {
            return;
        }

        const childStores = this.getChildrenStores();

        if (childStores && childStores.length > 0) {
            childStores.forEach((child) => child.updateSubtree(newState));
            const parentState = this.calculateParentStateByChildrenValue();
            this.setCheckBoxValue(parentState);
            return;
        }

        this.setCheckBoxValue(newState);
    }

    /** Returns computed total leaves count for this subtree. */
    public get totalLeafCount(): number {
        const children = this.getChildrenStores();
        if (!children || children.length === 0) {
            return 1;
        }
        return children.reduce((sum: number, child: CheckboxesTreeItemStore) => sum += child.totalLeafCount, 0);
    }

    /** Returns computed selected leaves count for this subtree. */
    public get selectedLeafCount(): number {
        const children = this.getChildrenStores();
        if (!children || children.length === 0) {
            return this.getCheckBoxValue() === TRISTATE_CHECKBOX_VALUES.CHECKED ? 1 : 0;
        }
        return children.reduce((sum: number, child: CheckboxesTreeItemStore) => sum += child.selectedLeafCount, 0);
    }

    /** Returns the number of direct children whose whole subtree is selected. */
    public get selectedChildCount(): number {
        return this.getChildrenStores()
            .filter((child) => child.totalLeafCount > 0 && child.selectedLeafCount === child.totalLeafCount)
            .length;
    }

    /** Returns the number of direct children with a selection that does not cover their whole subtree. */
    public get partiallySelectedChildCount(): number {
        return this.getChildrenStores()
            .filter((child) => child.selectedLeafCount > 0 && child.selectedLeafCount < child.totalLeafCount)
            .length;
    }

    /** Returns the selected units count in the node's counting mode. */
    public get selectedUnitCount(): number {
        return this._countsDirectChildren()
            ? this.selectedChildCount + this.partiallySelectedChildCount
            : this.selectedLeafCount;
    }

    /** Returns whether the whole subtree is selected, in the node's counting mode. */
    public get isFullySelected(): boolean {
        if (this._countsDirectChildren()) {
            const childrenCount = this.getChildrenStores().length;
            return childrenCount > 0 && this.selectedChildCount === childrenCount;
        }

        return this.totalLeafCount > 0 && this.selectedLeafCount === this.totalLeafCount;
    }

    /** True when the node aggregates direct children instead of descendant leaves. */
    private _countsDirectChildren(): boolean {
        return this._model.countMode === CHECKBOX_TREE_COUNT_MODE.DIRECT_CHILDREN;
    }

    /** Recursively computes and sets initial checkbox states for this subtree. */
    private computeAndSetInitialState(): TRISTATE_CHECKBOX_VALUES {
        const children = this.getChildrenStores();
        if (!children || children.length === 0) {
            const leafState = this.getCheckBoxValue() ?? TRISTATE_CHECKBOX_VALUES.UNCHECKED;
            this.setCheckBoxValue(leafState);
            return leafState;
        }
        const childStates = children.map((child) => child.computeAndSetInitialState());
        const total = childStates.length;
        const checked = childStates.filter((s) => s === TRISTATE_CHECKBOX_VALUES.CHECKED).length;
        const partial = childStates.filter((s) => s === TRISTATE_CHECKBOX_VALUES.PARTIALLY_CHECKED).length;
        const parentState = checked === total
            ? TRISTATE_CHECKBOX_VALUES.CHECKED
            : (checked > 0 || partial > 0 ? TRISTATE_CHECKBOX_VALUES.PARTIALLY_CHECKED : TRISTATE_CHECKBOX_VALUES.UNCHECKED);
        this.setCheckBoxValue(parentState);
        return parentState;
    }
}

export default CheckboxesTreeItemStore;
